import type { StepCell } from '@/stores/sequencerStore'

/**
 * Timing quantize for sub-step (`microOffset`) authoring.
 *
 * Cells keep their integer home-step index; only `microOffset` is
 * snapped. This preserves the user's on-grid mental model — a
 * quantized cell never jumps rows or slots. Values that would round
 * past the step boundary are capped at the last valid slot so a
 * hard snap doesn't silently teleport notes across steps.
 *
 * Subdivisions name how many slots per step the note can land in:
 *   1   → snap to 0 only (fully on-grid; microOffset = undefined)
 *   2   → 0 / 0.5
 *   3   → triplet feel
 *   4   → 0 / 0.25 / 0.5 / 0.75
 *   6   → sextuplet
 *   8   → fine
 *
 * Strength `s ∈ [0, 1]` interpolates between the original value
 * (s = 0, no change) and the fully-snapped value (s = 1). Values
 * below `EPSILON` after interpolation snap to on-grid to keep
 * persistence lean.
 */

export type QuantizeSubdivision = 1 | 2 | 3 | 4 | 6 | 8

export const QUANTIZE_SUBDIVISIONS: readonly {
  value: QuantizeSubdivision
  label: string
}[] = [
  { value: 1, label: '1 (on-grid)' },
  { value: 2, label: '1/2' },
  { value: 3, label: '1/3 (triplet)' },
  { value: 4, label: '1/4' },
  { value: 6, label: '1/6' },
  { value: 8, label: '1/8' },
]

export interface QuantizeOptions {
  subdivision: QuantizeSubdivision
  /** 0..1. 0 = no change, 1 = fully quantized. */
  strength: number
}

/**
 * Epsilon below which a quantized offset is treated as on-grid. Keeps
 * cells small by storing `microOffset: undefined` instead of a
 * near-zero float that would round-trip noisily through JSON.
 */
const ON_GRID_EPSILON = 1e-4

/**
 * Quantize a single microOffset value. Returns `undefined` for
 * on-grid values so the caller can clear the field.
 */
export function quantizeOffset(
  offset: number | undefined,
  opts: QuantizeOptions,
): number | undefined {
  const current = offset ?? 0
  if (current < 0 || current >= 1) return offset
  const strength = Math.max(0, Math.min(1, opts.strength))
  const n = opts.subdivision
  // Valid slots are {0/n, 1/n, ..., (n-1)/n}. Round the current
  // position to the nearest, then cap the last slot so we never
  // cross the boundary.
  const nearest = Math.round(current * n) / n
  const snapped = Math.min(nearest, (n - 1) / n)
  const interpolated = current + strength * (snapped - current)
  if (interpolated < ON_GRID_EPSILON) return undefined
  return interpolated
}

/**
 * Quantize a full row of StepCells in place (returns a new array;
 * non-mutating). Cells without microOffset pass through untouched.
 * `on` state and all other fields are preserved.
 */
export function quantizeRow(
  cells: readonly StepCell[],
  opts: QuantizeOptions,
): StepCell[] {
  return cells.map(cell => {
    if (!cell || cell.microOffset === undefined) return { ...cell }
    const next = quantizeOffset(cell.microOffset, opts)
    const out: StepCell = { ...cell }
    if (next === undefined) delete out.microOffset
    else out.microOffset = next
    return out
  })
}

/**
 * Quantize a map of `padIndex → StepCell[]`. Convenience for
 * applying the algorithm to an entire pattern or MIDI-pad steps
 * collection. Filters by `padFilter` when provided — used when the
 * user wants to quantize only the focused pad.
 */
export function quantizeGrid<K extends string | number>(
  grid: Record<K, StepCell[]>,
  opts: QuantizeOptions,
  padFilter?: (key: K) => boolean,
): Record<K, StepCell[]> {
  const out: Record<K, StepCell[]> = {} as Record<K, StepCell[]>
  for (const key of Object.keys(grid) as K[]) {
    const row = grid[key]
    if (padFilter && !padFilter(key)) {
      out[key] = row
      continue
    }
    out[key] = quantizeRow(row, opts)
  }
  return out
}
